"use client"
import { useEffect, useMemo, useState } from "react"
import {
  PLAN_KEYS,
  defaultGraphData,
} from "@/app/playbook/extrapolated-graph/graphConstants"
import type { GraphDatum } from "@/app/playbook/extrapolated-graph/types"
import {
  type PlanId,
  type ResolvedBranchNode,
  type ResolvedBranchesData,
} from "@/lib/branchTypes"
import { snapTimeToThirdOfYear } from "@/app/playbook/data/playbookScrollUtils"

const DATE_MARKER_RE = /:{3,}dateMarker[^\n]*\n\s*(\d{4}-\d{2}-\d{2})/g
const HOVER_TIMELINE_START_DELAY_MS = 1000
const HOVER_TIMELINE_UPDATE_MS = 100
const HOVER_TIMELINE_YEAR_DURATION_MS = 1100
// Branch plans (B/C/D/S) carry phase data on a 0.1-year grid; slow their
// sweep so every step gets at least one ~100ms frame (10 steps x 144ms).
const HOVER_TIMELINE_BRANCH_YEAR_DURATION_MS = 1440
// Stop the hover preview at the start of 2042 — the end of the forecast
// proper. The epilogue visuals that live past this point don't run during
// the hover anyway: PlaybookGlobe skips the 2041+ zoom-away camera
// keyframes while the hover preview is active.
const HOVER_TIMELINE_LOOP_END_DATE = "2042-01-01"
const HOVER_TIMELINE_LOOP_HOLD_MS = 3500
const AVERAGE_YEAR_MS = 365.25 * 24 * 60 * 60 * 1000

const getDateMs = (dateString: string) =>
  new Date(`${dateString}T00:00:00Z`).getTime()

const HOVER_TIMELINE_LOOP_END_MS = getDateMs(HOVER_TIMELINE_LOOP_END_DATE)

const getDateMsFromDatum = (datum: GraphDatum) => getDateMs(datum.date)

const extractDateMarkerTimes = (markdown: string | undefined): number[] => {
  if (!markdown) return []
  return Array.from(markdown.matchAll(DATE_MARKER_RE))
    .map(match => getDateMs(match[1]))
    .filter(Number.isFinite)
}

const getNodeDateMarkerTimes = (node: ResolvedBranchNode): number[] => [
  ...extractDateMarkerTimes(node.content?.markdown),
  ...(
    (node.sections ?? []) as Array<{ content?: { markdown?: string } }>
  ).flatMap(section =>
    extractDateMarkerTimes(section.content?.markdown)
  ),
]

const getPrimaryContinuationTargetId = (
  node: ResolvedBranchNode,
  nodes: Record<string, ResolvedBranchNode>,
) => {
  const options = node.branchPoints?.[0]?.options ?? []
  const primaryOption = options.find(option =>
    option.targetId &&
    (option.primary || nodes[option.targetId]?.primary)
  )
  return primaryOption?.targetId ?? null
}

const collectBranchDateMarkerTimes = (
  targetId: string,
  nodes: Record<string, ResolvedBranchNode>,
) => {
  const visitedNodeIds = new Set<string>()
  const dateMarkerTimes: number[] = []
  let currentTargetId: string | null = targetId

  while (currentTargetId && !visitedNodeIds.has(currentTargetId)) {
    visitedNodeIds.add(currentTargetId)
    const node = nodes[currentTargetId]
    if (!node) break
    dateMarkerTimes.push(...getNodeDateMarkerTimes(node))
    currentTargetId = getPrimaryContinuationTargetId(node, nodes)
  }

  return dateMarkerTimes
}

const getCapabilityPreviewTimesForPlan = (planId: PlanId | null) => {
  if (!planId) return []
  const planBUs = defaultGraphData.planBUs
  const planSeries =
    planId === "B" && planBUs && planBUs.length > 0
      ? planBUs
      : defaultGraphData[PLAN_KEYS[planId]]

  return [
    ...defaultGraphData.shared,
    ...(planSeries ?? []),
  ].map(getDateMsFromDatum)
}

const dedupeAndSortTimes = (times: number[]) =>
  Array.from(new Set(times.filter(Number.isFinite)))
    .sort((firstTime, secondTime) => firstTime - secondTime)

const getPreviewTimelineTimes = ({
  branchData,
  targetId,
  planId,
}: {
  branchData: ResolvedBranchesData
  targetId: string
  planId: PlanId | null
}) => {
  const branchMarkerTimes = dedupeAndSortTimes(
    collectBranchDateMarkerTimes(targetId, branchData.nodes)
  )
  if (branchMarkerTimes.length > 1) return branchMarkerTimes
  return dedupeAndSortTimes(getCapabilityPreviewTimesForPlan(planId))
}

export const useBranchHoverAutoplayDate = ({
  branchData,
  hoveredBranchTargetId,
  hoveredPlan,
  baseDate,
}: {
  branchData: ResolvedBranchesData
  hoveredBranchTargetId: string | null
  hoveredPlan: PlanId | null
  baseDate: Date
}) => {
  const previewTimeline = useMemo(() => {
    // Any plan with a hovered branch target animates: B/C/D/S stubs carry
    // their own dateMarkers (falling back to the plan's capability-curve
    // dates), so the HUD previews every branch, not just Plan A.
    if (!hoveredBranchTargetId || !hoveredPlan) return null
    const timelineTimes = getPreviewTimelineTimes({
      branchData,
      targetId: hoveredBranchTargetId,
      planId: hoveredPlan,
    })
    const baseTime = baseDate.getTime()
    const futureTimes = timelineTimes.filter(time => time > baseTime)
    const lastFutureTime = futureTimes[futureTimes.length - 1]
    if (!lastFutureTime) return null
    const endTime = Math.min(lastFutureTime, HOVER_TIMELINE_LOOP_END_MS)
    if (endTime <= baseTime) return null
    const isBranchPlan = hoveredPlan !== "A"
    return {
      key: `${hoveredBranchTargetId}:${hoveredPlan ?? "none"}:${baseTime}`,
      startTime: baseTime,
      endTime,
      // Branch previews step through every 0.1-year phase; Plan A keeps its
      // original smooth sweep.
      snapToPhaseGrid: isBranchPlan,
      msPerMs: AVERAGE_YEAR_MS / (isBranchPlan
        ? HOVER_TIMELINE_BRANCH_YEAR_DURATION_MS
        : HOVER_TIMELINE_YEAR_DURATION_MS),
    }
  }, [baseDate, branchData, hoveredBranchTargetId, hoveredPlan])

  const [previewDate, setPreviewDate] = useState<Date | null>(null)

  useEffect(() => {
    if (!previewTimeline) {
      setPreviewDate(null)
      return
    }

    setPreviewDate(null)
    let startDelayTimeoutId: number | null = null
    let timeoutId: number | null = null
    const animationStart = performance.now() + HOVER_TIMELINE_START_DELAY_MS
    const tick = () => {
      const elapsed = Math.max(0, performance.now() - animationStart)
      const timelineDuration = previewTimeline.endTime - previewTimeline.startTime
      const timelineAnimationDuration = timelineDuration / previewTimeline.msPerMs
      const loopDuration = timelineAnimationDuration + HOVER_TIMELINE_LOOP_HOLD_MS
      const elapsedLoopMs = elapsed % loopDuration
      const rawTime = elapsedLoopMs >= timelineAnimationDuration
        ? previewTimeline.endTime
        : previewTimeline.startTime + elapsedLoopMs * previewTimeline.msPerMs
      const nextTime = previewTimeline.snapToPhaseGrid && rawTime !== previewTimeline.endTime
        ? Math.max(previewTimeline.startTime, snapTimeToThirdOfYear(rawTime))
        : rawTime
      setPreviewDate(previousDate =>
        previousDate?.getTime() === Math.round(nextTime)
          ? previousDate
          : new Date(Math.round(nextTime))
      )
      timeoutId = window.setTimeout(tick, HOVER_TIMELINE_UPDATE_MS)
    }

    startDelayTimeoutId = window.setTimeout(tick, HOVER_TIMELINE_START_DELAY_MS)
    return () => {
      if (startDelayTimeoutId !== null) window.clearTimeout(startDelayTimeoutId)
      if (timeoutId !== null) window.clearTimeout(timeoutId)
    }
  }, [previewTimeline])

  return previewDate
}
